import { useCallback, useMemo, useState } from "react";
import { injectIntl } from "react-intl";
import TopMenu from "components/InventoryVariants/TopMenu";
import List from "components/common/ListWithMoreOptions";
import ModalFullScreen from "components/common/ModalFullScreen";
import { useItemVariants } from "graphql/useItemExtras";
import { useItemVariantMutations } from "graphql/useItemExtraMutations";
import { Spin } from "antd";
import BottomPageSelectorWithPagination, {
  usePagination,
} from "components/common/BottomPageSelectorWithPagination";
import moment from "moment";
import { usePaginatedData } from "helpers/hooks";
import { useTranslation } from "react-i18next";
import { useMessaging } from "context/MessagingContext";
import s from "./styles";
import AddVariant from "./AddVariant";

function InventoryVariants() {
  const { t } = useTranslation();

  const [addCategoryModalOpen, setAddItemModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const { deleteItemVariant } = useItemVariantMutations();

  const { setInfoMessage, setConfirmMessage, setErrorMessage } = useMessaging();

  const { itemVariants, loading } = useItemVariants();

  const { sorting, setSorting, search, setSearch, pagination, setPagination } =
    usePagination("name");

  const variantNames = itemVariants.map((variant) => variant.name);

  function changeSearch(v) {
    setSearch(v);
    setPagination({ ...pagination, page: 0 });
  }

  const data = useMemo(
    () =>
      Object.values(itemVariants).filter((c) =>
        c.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
      ),
    [itemVariants, search]
  );

  const dataToShow = usePaginatedData(data, pagination, sorting, (variant) => [
    variant.name,
    variant.variants.map((v) => v.name).join(", "),
    moment(variant.updateDatetime).format("lll"),
  ]);

  const onUpdateSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.InventoryVariants.variantEdit"),
      info1: t("containers.InventoryVariants.findVariant"),
    });
  }, []);
  const onAddSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.InventoryVariants.newVariant"),
      info1: t("containers.InventoryVariants.variantDB"),
    });
  }, []);

  const onDeleteRequest = useCallback((customer) => {
    setConfirmMessage({
      titleAlert: t("containers.InventoryVariants.delete"),
      confirm1: `${t("containers.InventoryVariants.deleteVariant")} ${
        customer?.name
      }?`,
      confirm2: t("containers.InventoryVariants.actionCannotBeUndone"),
      onConfirm: () =>
        deleteItemVariant(customer).catch((e) => setErrorMessage({ error: e })),
    });
  }, []);

  return (
    <s.PageContainer>
      <TopMenu
        changeSearchValue={(v) => changeSearch(v)}
        addCategory={() => setAddItemModalOpen(true)}
        loading={loading}
      />

      <s.ListContainer>
        {!loading && (
          <List
            width="600px"
            handleEdit={(variant) => {
              // todo: change for _.cloneDeep
              const copy = JSON.parse(JSON.stringify(variant.element));
              function cleanCopy(copy) {
                delete copy.__typename;
                copy.variants?.forEach((variant) => {
                  delete variant.__typename;
                  variant.items = variant?.items?.map((item) => {
                    delete item.__typename;
                    return item;
                  });
                });
              }
              cleanCopy(copy);
              setItemToEdit(copy);
              setAddItemModalOpen(true);
            }}
            handleDeleteWorker={(variant) => {
              onDeleteRequest(variant.element);
            }}
            data={dataToShow}
            columns={[
              {
                title: t("containers.InventoryVariants.grouper"),
                sortable: true,
                columnId: "name",
              },
              {
                title: t("containers.InventoryVariants.variants"),
              },
              {
                title: t("containers.InventoryVariants.updateDatetime"),
                sortable: true,
                columnId: "updateDatetime",
              },
              {
                title: "",
              },
            ]}
            sorting={sorting}
            changeSorting={(columnId, order) => setSorting({ columnId, order })}
            tableStyle={{ gridTemplateColumns: "10fr 15fr 15fr 3fr" }}
          />
        )}
      </s.ListContainer>

      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Spin
            style={{ color: "#000" }}
            size="large"
            tip={t("containers.InventoryVariants.loading")}
          />
        </div>
      )}

      <BottomPageSelectorWithPagination
        pagination={pagination}
        setPagination={setPagination}
        length={itemVariants.length}
      />

      {addCategoryModalOpen && (
        <ModalFullScreen>
          <AddVariant
            variantNames={variantNames}
            closeModal={() => {
              setAddItemModalOpen(false);
              setItemToEdit(null);
            }}
            itemToEdit={itemToEdit}
            onUpdateSuccess={onUpdateSuccess}
            onAddSuccess={onAddSuccess}
          />
        </ModalFullScreen>
      )}
    </s.PageContainer>
  );
}

export default injectIntl(InventoryVariants);
